/**
 * VoiceInput — microphone button using Web Speech API.
 *
 * Props:
 *   onTranscript(text) — called with recognised text (appended to current input)
 *   disabled           — boolean (mic disabled while AI is responding)
 *   lang               — BCP-47 language tag (default "ru-RU")
 *
 * Behaviour:
 *   - Click to start listening; click again or after final result to stop.
 *   - Shows animated ring while recording.
 *   - Silently hides if SpeechRecognition is not supported (Chrome/Edge only).
 */

import { useRef, useState, useEffect } from 'react'
import { Mic, MicOff } from 'lucide-react'

export default function VoiceInput({ onTranscript, disabled = false, lang = 'ru-RU' }) {
    const [listening, setListening] = useState(false)
    const [supported, setSupported] = useState(false)
    const recogRef = useRef(null)

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        setSupported(Boolean(SpeechRecognition))
    }, [])

    if (!supported) return null

    const toggle = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (listening) {
            recogRef.current?.stop()
            setListening(false)
            return
        }

        const recog = new SpeechRecognition()
        recog.lang = lang
        recog.interimResults = false
        recog.maxAlternatives = 1
        recogRef.current = recog

        recog.onresult = (e) => {
            const text = e.results[0]?.[0]?.transcript || ''
            if (text) onTranscript?.(text)
        }
        recog.onerror = () => setListening(false)
        recog.onend = () => setListening(false)

        recog.start()
        setListening(true)
    }

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            aria-label={listening ? 'Остановить запись' : 'Голосовой ввод'}
            title={listening ? 'Остановить запись' : 'Голосовой ввод (русский)'}
            style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: `1.5px solid ${listening ? 'var(--color-danger)' : 'var(--color-border)'}`,
                background: listening ? 'var(--color-danger-bg)' : 'var(--color-bg-card)',
                color: listening ? 'var(--color-danger)' : 'var(--color-text-secondary)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                outline: listening ? '2px solid var(--color-danger)' : 'none',
                outlineOffset: 2,
                animation: listening ? 'voice-pulse 1.2s ease-in-out infinite' : 'none',
            }}
        >
            {listening ? <MicOff size={14} /> : <Mic size={14} />}
        </button>
    )
}
